import { Component, ElementRef, ViewChild, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LiveCallService } from '../core/live-call.service';
import { SiteConfigService } from '../core/site-config.service';
import { LocalDatePipe } from './local-date.pipe';

@Component({
  selector: 'app-live-call-overlay',
  imports: [FormsModule, LocalDatePipe],
  template: `
    @if (call.handoffNotice()) {
      <div class="call-handoff-notice" role="status">{{ call.handoffNotice() }}</div>
    }
    @if (call.alerts().length && !call.call()) {
      <section class="call-alerts" aria-label="Cevapsız aramalar">
        @for (alert of call.alerts(); track alert.callID) {
          <article>
            <span class="missed-icon">↙</span>
            <span>
              <strong>{{ alertName(alert) }}</strong>
              <small>Cevapsız {{ alert.callType === 'video' ? 'görüntülü' : 'sesli' }} arama</small>
            </span>
            @if (alert.callbackAvailable) {
              <button class="callback" type="button" (click)="call.callback(alert)">Geri ara</button>
            }
            <button class="dismiss" type="button" (click)="call.dismissAlert(alert.callID)" aria-label="Kapat">×</button>
          </article>
        }
      </section>
    }
    @if (call.call(); as item) {
      <aside
        class="call-shell"
        [class.is-minimized]="minimized()"
        [class.is-expanded]="expanded()"
        [class.is-voice]="item['callType'] !== 'video'"
      >
        @if (minimized()) {
          <button class="mini-call" type="button" (click)="restorePanel()" aria-label="Görüşmeyi aç">
            <span class="mini-avatar">
              @if (participantImage()) {
                <img [src]="participantImage()" alt="" />
              } @else {
                {{ initials() }}
              }
            </span>
            <span>
              <strong>{{ participantName() }}</strong>
              <small>{{ call.formatDuration() }} · {{ item['callType'] === 'video' ? 'Görüntülü' : 'Sesli' }}</small>
            </span>
            <i class="live-dot"></i>
          </button>
        } @else {
          <header class="call-head">
            <div class="identity">
              <span class="avatar">
                @if (participantImage()) {
                  <img [src]="participantImage()" alt="" />
                } @else {
                  {{ initials() }}
                }
              </span>
              <span>
                <strong>{{ participantName() }}</strong>
                <small>{{ statusLabel() }}</small>
              </span>
            </div>
            <div class="call-metrics">
              @if (call.mode() === 'active') {
                <b>{{ call.formatDuration() }}</b>
                @if (call.isSeller()) {
                  <small>{{ item['creditsPerMinute'] || 0 }} kredi/dk</small>
                } @else {
                  <small>{{ call.projectedCredits() }} / {{ call.creditBalance() ?? 0 }} kredi</small>
                }
              }
            </div>
            <div class="window-actions">
              @if (call.mode() === 'active') {
                <button type="button" (click)="toggleChat()" [class.active]="chatOpen()" aria-label="Mesajlar">
                  <svg viewBox="0 0 24 24"><path d="M4 5h16v11H8l-4 3V5Z"/></svg>
                  @if (!chatOpen() && call.messages().length) { <i>{{ call.messages().length }}</i> }
                </button>
                <button type="button" (click)="toggleExpanded()" aria-label="Görünümü değiştir">
                  @if (expanded()) {
                    <svg viewBox="0 0 24 24"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>
                  } @else {
                    <svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>
                  }
                </button>
                <button type="button" (click)="minimize()" aria-label="Küçült">
                  <svg viewBox="0 0 24 24"><path d="M5 12h14"/></svg>
                </button>
              }
            </div>
          </header>

          <div class="call-body" [class.with-chat]="chatOpen()">
            <section class="media-stage">
              <div id="global-call-remote" class="remote-video"></div>
              @if (item['callType'] !== 'video' || call.remoteCameraOff()) {
                <div class="camera-placeholder remote-placeholder">
                  <span class="hero-avatar">
                    @if (participantImage()) {
                      <img [src]="participantImage()" alt="" />
                    } @else {
                      {{ initials() }}
                    }
                  </span>
                  <strong>{{ participantName() }}</strong>
                  @if (call.remoteCameraOff() && call.mode() === 'active') { <small>Kamera kapalı</small> }
                </div>
              }
              @if (call.remoteMuted() && call.mode() === 'active') {
                <span class="remote-state">
                  <svg viewBox="0 0 24 24"><path d="M9 9v3a3 3 0 0 0 4.6 2.5M15 9V5a3 3 0 0 0-5.8-1M5 10v2a7 7 0 0 0 11.7 5.2M12 19v3M8 22h8M3 3l18 18"/></svg>
                  Mikrofon kapalı
                </span>
              }

              @if (item['callType'] === 'video' && call.mode() === 'active') {
                <div
                  #localPreview
                  class="local-preview"
                  [class.camera-off]="call.cameraOff()"
                  (pointerdown)="startPreviewDrag($event)"
                >
                  <div id="global-call-local"></div>
                  @if (call.cameraOff()) {
                    <span>
                      <svg viewBox="0 0 24 24"><path d="M15 10l4-3v10l-4-3M4 6h11v12H4zM3 3l18 18"/></svg>
                    </span>
                  }
                </div>
              }

              @if (call.mode() === 'incoming') {
                <div class="incoming-copy">
                  <span>Gelen {{ item['callType'] === 'video' ? 'görüntülü' : 'sesli' }} arama</span>
                  <h2>{{ participantName() }}</h2>
                  <p>{{ item['creditsPerMinute'] || 0 }} kredi / dakika</p>
                </div>
              } @else if (call.mode() === 'ringing' || call.mode() === 'permission') {
                <div class="incoming-copy">
                  <span>{{ call.mode() === 'permission' ? 'Cihazlar hazırlanıyor' : 'Aranıyor' }}</span>
                  <h2>{{ participantName() }}</h2>
                  <p>{{ item['creditsPerMinute'] || 0 }} kredi / dakika</p>
                </div>
              } @else if (call.mode() === 'ended') {
                <div class="call-summary">
                  <span>Görüşme tamamlandı</span>
                  <h2>{{ call.formatDuration() }}</h2>
                  @if (call.summary()?.['isSeller']) {
                    <p><b>{{ call.summary()?.['billedCredits'] || 0 }} kredi</b> kazandın.</p>
                  } @else {
                    <p><b>{{ call.summary()?.['billedCredits'] || 0 }} kredi</b> harcadın.</p>
                  }
                  <button type="button" (click)="call.dismissSummary()">Kapat</button>
                </div>
              }

              @if (call.error()) {
                <button class="call-error" type="button" (click)="call.recoverMedia()">
                  {{ call.error() }}
                  @if (call.mode() === 'active') { <b>Tekrar dene</b> }
                </button>
              }

              <div class="call-controls">
                @if (call.mode() === 'incoming') {
                  <button class="round reject" type="button" (click)="call.decline()" aria-label="Reddet">
                    <svg viewBox="0 0 24 24"><path d="M6.6 10.8a15 15 0 0 1 10.8 0l1.2-2.4 3 1.5-2.4 4.8-3.8-1.9.7-1.4a12 12 0 0 0-8.2 0l.7 1.4-3.8 1.9-2.4-4.8 3-1.5 1.2 2.4Z"/></svg>
                  </button>
                  <button class="round accept" type="button" (click)="call.accept()" aria-label="Kabul et">
                    <svg viewBox="0 0 24 24"><path d="M6.6 13.2a15 15 0 0 0 10.8 0l1.2 2.4 3-1.5-2.4-4.8-3.8 1.9.7 1.4a12 12 0 0 1-8.2 0l.7-1.4-3.8-1.9-2.4 4.8 3 1.5 1.2-2.4Z"/></svg>
                  </button>
                  <button class="round neutral" type="button" (click)="call.blockCaller()" aria-label="Engelle">
                    <svg viewBox="0 0 24 24"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm-5.6 9a5.6 5.6 0 0 1 1.1-3.4l7.9 7.9A6 6 0 0 1 6.4 12Zm10.1 3.4-7.9-7.9a6 6 0 0 1 7.9 7.9Z"/></svg>
                  </button>
                } @else if (call.mode() === 'active') {
                  <button class="round" [class.off]="call.muted()" type="button" (click)="call.toggleMute()" aria-label="Mikrofon">
                    <svg viewBox="0 0 24 24"><path d="M9 5a3 3 0 0 1 6 0v7a3 3 0 0 1-6 0V5ZM5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8"/></svg>
                  </button>
                  @if (item['callType'] === 'video') {
                    <button class="round" [class.off]="call.cameraOff()" type="button" (click)="call.toggleCamera()" aria-label="Kamera">
                      <svg viewBox="0 0 24 24"><path d="M4 6h11v12H4zM15 10l5-3v10l-5-3"/></svg>
                    </button>
                  }
                  <button class="round chat-control" [class.active]="chatOpen()" type="button" (click)="toggleChat()" aria-label="Mesaj">
                    <svg viewBox="0 0 24 24"><path d="M4 5h16v11H8l-4 3V5Z"/></svg>
                  </button>
                  <button class="round reject" type="button" (click)="call.end()" aria-label="Bitir">
                    <svg viewBox="0 0 24 24"><path d="M6.6 10.8a15 15 0 0 1 10.8 0l1.2-2.4 3 1.5-2.4 4.8-3.8-1.9.7-1.4a12 12 0 0 0-8.2 0l.7 1.4-3.8 1.9-2.4-4.8 3-1.5 1.2 2.4Z"/></svg>
                  </button>
                } @else if (call.mode() === 'ringing') {
                  <button class="round reject" type="button" (click)="call.end()" aria-label="İptal">
                    <svg viewBox="0 0 24 24"><path d="M6.6 10.8a15 15 0 0 1 10.8 0l1.2-2.4 3 1.5-2.4 4.8-3.8-1.9.7-1.4a12 12 0 0 0-8.2 0l.7 1.4-3.8 1.9-2.4-4.8 3-1.5 1.2 2.4Z"/></svg>
                  </button>
                }
              </div>
            </section>

            @if (chatOpen() && call.mode() === 'active') {
              <section class="call-chat">
                <header>
                  <strong>Mesajlar</strong>
                  <button type="button" (click)="chatOpen.set(false)" aria-label="Mesajları kapat">×</button>
                </header>
                <div #messageList class="message-list">
                  @if (!call.messages().length) {
                    <p class="empty-chat">Görüşme sırasında mesaj ve fotoğraf paylaşabilirsin.</p>
                  }
                  @for (message of call.messages(); track message.messageID) {
                    <article [class.mine]="call.isOwnMessage(message)">
                      @for (attachment of message.attachments; track attachment.callFileID) {
                        @if (attachment.isImage && attachment.previewUrl) {
                          <button class="message-image" type="button" (click)="call.openAttachment(attachment)">
                            <img [src]="attachment.previewUrl" [alt]="attachment.fileName || 'Görsel'" />
                          </button>
                        }
                      }
                      @if (message.message) { <p>{{ message.message }}</p> }
                      <time>{{ message.createdAt | localDate:'time' }}</time>
                    </article>
                  }
                </div>
                @if (call.uploads().length) {
                  <div class="upload-previews">
                    @for (upload of call.uploads(); track upload.uploadID) {
                      <button type="button" (click)="call.removeUpload(upload.uploadID)">
                        <img [src]="upload.previewUrl" [alt]="upload.fileName" /><i>×</i>
                      </button>
                    }
                  </div>
                }
                <form class="composer" (submit)="sendMessage($event)">
                  <label [class.busy]="call.uploading()" aria-label="Fotoğraf ekle">
                    <input type="file" accept="image/*" multiple (change)="selectImages($event)" />
                    <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
                  </label>
                  <input
                    name="callMessage"
                    [(ngModel)]="message"
                    autocomplete="off"
                    placeholder="Mesaj yaz..."
                    (keydown.enter)="sendOnEnter($event)"
                  />
                  <button type="submit" [disabled]="call.sending() || (!message.trim() && !call.uploads().length)" aria-label="Gönder">
                    <svg viewBox="0 0 24 24"><path d="m4 4 17 8-17 8 3-8-3-8Zm3 8h14"/></svg>
                  </button>
                </form>
              </section>
            }
          </div>
        }
      </aside>
    }
  `,
  styles: [`
    :host{position:relative;z-index:240}
    .call-handoff-notice{position:fixed;z-index:260;top:18px;left:50%;transform:translateX(-50%);padding:12px 18px;border:1px solid #ffffff24;border-radius:999px;background:#17211df2;color:#fff;font-size:13px;font-weight:800;box-shadow:0 16px 45px #0007;backdrop-filter:blur(16px);animation:handoff-in .28s ease both}
    .call-alerts{position:fixed;z-index:245;right:18px;top:18px;width:min(390px,calc(100vw - 36px));display:grid;gap:8px}.call-alerts article{display:flex;align-items:center;gap:11px;padding:11px 12px;border:1px solid #ffffff24;border-radius:18px;background:#17211df2;color:#fff;box-shadow:0 16px 45px #0007;backdrop-filter:blur(16px)}.call-alerts article>span:nth-child(2){display:grid;gap:2px;min-width:0;flex:1}.call-alerts small{color:#aab8b1;font-size:12px}.missed-icon{display:grid;place-items:center;width:38px;height:38px;border-radius:50%;background:#e94f6227;color:#ff7a8b;font-size:22px}.call-alerts button{border:0;cursor:pointer}.callback{padding:8px 11px;border-radius:999px;background:#64d6a6;color:#102219;font-weight:800}.dismiss{width:30px;height:30px;border-radius:50%;background:#ffffff0d;color:#fff;font-size:20px}
    .call-shell{--call-bg:#101714;--call-panel:#17211d;--call-line:#ffffff1f;--call-text:#f7faf8;--call-muted:#aab8b1;position:fixed;z-index:240;right:18px;top:18px;width:min(860px,calc(100vw - 36px));height:min(650px,calc(100dvh - 36px));color:var(--call-text);background:var(--call-bg);border:1px solid var(--call-line);border-radius:28px;box-shadow:0 28px 80px #0008;overflow:hidden;isolation:isolate;transition:width .35s ease,height .35s ease,inset .35s ease,border-radius .35s ease}
    .call-shell.is-expanded{inset:12px;width:auto;height:auto;border-radius:22px}
    .call-shell.is-minimized{top:18px;right:18px;width:280px;height:72px;border-radius:24px;overflow:visible}
    button{font:inherit;color:inherit}
    svg{width:22px;height:22px;fill:none;stroke:currentColor;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}
    .mini-call{width:100%;height:100%;border:1px solid #ffffff24;border-radius:24px;background:#101714;color:#fff;display:flex;align-items:center;gap:12px;padding:9px 14px;text-align:left;box-shadow:0 18px 50px #0007;cursor:pointer}
    .mini-call span:nth-child(2){display:grid;gap:3px;min-width:0}.mini-call strong,.mini-call small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.mini-call small{font-size:12px;color:#aab8b1}.mini-avatar,.avatar{display:grid;place-items:center;flex:0 0 auto;border-radius:50%;overflow:hidden;background:linear-gradient(135deg,#65d5a3,#277e62);font-weight:900}.mini-avatar{width:46px;height:46px}.mini-avatar img,.avatar img,.hero-avatar img{width:100%;height:100%;object-fit:cover}.live-dot{width:9px;height:9px;border-radius:50%;background:#4ee79e;box-shadow:0 0 0 5px #4ee79e22;margin-left:auto;animation:pulse 1.4s infinite}
    .call-head{height:76px;display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);align-items:center;padding:0 18px;border-bottom:1px solid var(--call-line);background:#101714ee;position:relative;z-index:5}
    .identity{display:flex;align-items:center;gap:12px;min-width:0}.identity>span:last-child{display:grid;gap:3px;min-width:0}.identity strong{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.identity small{font-size:12px;color:var(--call-muted)}.avatar{width:44px;height:44px}
    .call-metrics{text-align:center;display:grid;gap:2px}.call-metrics b{font-variant-numeric:tabular-nums;letter-spacing:.05em}.call-metrics small{font-size:11px;color:var(--call-muted);text-transform:uppercase;letter-spacing:.08em}
    .window-actions{display:flex;justify-content:flex-end;gap:7px}.window-actions button,.call-chat header button{position:relative;width:38px;height:38px;display:grid;place-items:center;border-radius:50%;border:1px solid var(--call-line);background:#ffffff0b;cursor:pointer}.window-actions button:hover,.window-actions button.active{background:#ffffff1d}.window-actions i{position:absolute;right:-3px;top:-4px;display:grid;place-items:center;min-width:18px;height:18px;padding:0 4px;border-radius:10px;background:#ff5f7d;font-size:10px;font-style:normal}
    .call-body{height:calc(100% - 76px);display:grid;grid-template-columns:1fr;min-height:0}.call-body.with-chat{grid-template-columns:minmax(0,1fr) 330px}
    .media-stage{position:relative;overflow:hidden;min-width:0;background:radial-gradient(circle at 50% 25%,#2b4b40 0,#101713 55%,#080d0b 100%)}
    .remote-video{position:absolute;inset:0}.remote-video :is(video,div){width:100%!important;height:100%!important;object-fit:contain!important}
    .camera-placeholder{position:absolute;inset:0;display:grid;place-content:center;justify-items:center;gap:12px;background:radial-gradient(circle at 50% 32%,#294a3d,#101713 64%);text-align:center}.camera-placeholder small{color:var(--call-muted)}
    .hero-avatar{width:104px;height:104px;border-radius:50%;display:grid;place-items:center;overflow:hidden;background:linear-gradient(145deg,#5ed5a0,#1b6950);font-size:36px;font-weight:900;box-shadow:0 0 0 10px #ffffff0c}
    .local-preview{position:absolute;z-index:6;right:18px;top:18px;width:clamp(105px,19%,180px);aspect-ratio:3/4;border:2px solid #ffffffb8;border-radius:20px;overflow:hidden;background:#17211d;box-shadow:0 14px 35px #0008;touch-action:none;cursor:grab}.local-preview:active{cursor:grabbing}.local-preview>div,.local-preview :is(video,div){width:100%!important;height:100%!important;object-fit:cover!important}.local-preview.camera-off>span{position:absolute;inset:0;display:grid;place-items:center;background:#17211d}.local-preview.camera-off svg{width:32px;height:32px}
    .remote-state{position:absolute;left:16px;top:16px;display:flex;align-items:center;gap:7px;padding:8px 11px;border-radius:999px;background:#0009;backdrop-filter:blur(10px);font-size:12px}.remote-state svg{width:16px;height:16px}
    .incoming-copy{position:absolute;inset:0;display:grid;place-content:center;text-align:center;padding:24px;z-index:2}.incoming-copy span{font-size:13px;text-transform:uppercase;letter-spacing:.14em;color:#86e2b9}.incoming-copy h2{font-size:clamp(30px,5vw,56px);margin:10px 0 4px}.incoming-copy p{color:var(--call-muted);margin:0}
    .call-summary{position:absolute;inset:0;z-index:10;display:grid;place-content:center;justify-items:center;text-align:center;background:radial-gradient(circle at 50% 38%,#26483b,#0e1512 68%);padding:24px}.call-summary span{color:#83dfb6;text-transform:uppercase;letter-spacing:.14em;font-size:12px}.call-summary h2{font-size:clamp(42px,8vw,82px);margin:12px 0 2px;font-variant-numeric:tabular-nums}.call-summary p{color:#becbc5;font-size:18px}.call-summary b{color:#fff}.call-summary button{margin-top:12px;border:0;border-radius:999px;background:#63d6a5;color:#102119;font-weight:900;padding:13px 28px;cursor:pointer}
    .call-controls{position:absolute;z-index:9;left:50%;bottom:18px;transform:translateX(-50%);display:flex;gap:12px;padding:9px;border-radius:999px;background:#050807a8;border:1px solid #ffffff18;backdrop-filter:blur(14px)}
    .round{width:50px;height:50px;display:grid;place-items:center;border:0;border-radius:50%;background:#f4f7f5;color:#15201b;cursor:pointer;transition:transform .2s,background .2s}.round:hover{transform:translateY(-2px)}.round.off,.round.active{background:#f2b84b}.round.reject{background:#f05566;color:#fff}.round.accept{background:#43d58d;color:#07150e}.round.neutral{background:#ffffff20;color:#fff}.round svg{width:24px;height:24px}
    .call-error{position:absolute;z-index:8;left:50%;bottom:90px;transform:translateX(-50%);border:1px solid #ff9db044;background:#4a1b26dc;color:#ffdce3;border-radius:14px;padding:9px 13px;font-size:12px;cursor:pointer}.call-error b{margin-left:8px;color:#fff}
    .call-chat{height:100%;min-height:0;display:grid;grid-template-rows:58px minmax(0,1fr) auto auto;border-left:1px solid var(--call-line);background:var(--call-panel)}.call-chat>header{display:flex;align-items:center;justify-content:space-between;padding:0 14px;border-bottom:1px solid var(--call-line)}.call-chat header button{font-size:25px}
    .message-list{min-height:0;overflow:auto;overscroll-behavior:contain;padding:14px;display:flex;flex-direction:column;gap:9px;scrollbar-width:thin}.empty-chat{margin:auto;text-align:center;color:var(--call-muted);font-size:13px;max-width:220px;line-height:1.55}
    .message-list article{align-self:flex-start;max-width:86%;display:grid;gap:5px;padding:9px 11px;border-radius:5px 16px 16px 16px;background:#ffffff0d}.message-list article.mine{align-self:flex-end;border-radius:16px 5px 16px 16px;background:#28765a}.message-list p{margin:0;white-space:pre-wrap;overflow-wrap:anywhere;font-size:14px;line-height:1.42}.message-list time{justify-self:end;font-size:9px;color:#ffffff9d}.message-image{border:0;padding:0;background:none;border-radius:11px;overflow:hidden;cursor:pointer}.message-image img{display:block;width:100%;max-height:210px;object-fit:cover}
    .upload-previews{display:flex;gap:7px;overflow:auto;padding:8px 12px 0}.upload-previews button{position:relative;flex:0 0 58px;height:58px;border:0;border-radius:11px;padding:0;overflow:hidden;background:#0b100e}.upload-previews img{width:100%;height:100%;object-fit:cover}.upload-previews i{position:absolute;right:3px;top:3px;width:18px;height:18px;display:grid;place-items:center;border-radius:50%;background:#000b;color:#fff;font-style:normal}
    .composer{display:grid;grid-template-columns:42px minmax(0,1fr) 42px;gap:8px;padding:11px;border-top:1px solid var(--call-line)}.composer label,.composer button{width:42px;height:42px;display:grid;place-items:center;border-radius:50%;border:0;background:#ffffff10;cursor:pointer}.composer label.busy{opacity:.45}.composer label input{display:none}.composer input{min-width:0;border:1px solid #ffffff18;border-radius:22px;background:#090d0b;color:#fff;padding:0 15px;outline:0}.composer input:focus{border-color:#62d7a5}.composer button{background:#62d7a5;color:#102219}.composer button:disabled{opacity:.35;cursor:not-allowed}
    @keyframes pulse{50%{transform:scale(.75);opacity:.6}}@keyframes handoff-in{from{opacity:0;transform:translate(-50%,-12px)}}
    @media(max-width:760px){
      .call-alerts{right:8px;top:8px;width:calc(100vw - 16px)}
      .call-shell{right:8px;top:8px;width:calc(100vw - 16px);height:min(620px,calc(100dvh - 80px));border-radius:22px}.call-shell.is-expanded{inset:0 0 68px;border-radius:0}.call-shell.is-minimized{right:8px;top:8px;width:min(280px,calc(100vw - 16px));height:66px}.call-head{height:68px;padding:0 11px;grid-template-columns:minmax(0,1fr) auto auto}.call-body{height:calc(100% - 68px)}.call-body.with-chat{grid-template-columns:1fr}.call-body.with-chat .media-stage{display:none}.call-chat{border-left:0}.call-metrics small{display:none}.avatar{width:38px;height:38px}.identity{gap:8px}.identity strong{max-width:120px}.window-actions{gap:4px}.window-actions button{width:34px;height:34px}.window-actions button:first-child{display:none}.local-preview{width:105px;right:12px;top:12px}.round{width:47px;height:47px}.call-controls{bottom:12px}.remote-state{top:12px;left:12px}
    }
  `]
})
export class LiveCallOverlayComponent {
  protected readonly call = inject(LiveCallService);
  private readonly site = inject(SiteConfigService);

  @ViewChild('localPreview') private localPreview?: ElementRef<HTMLElement>;
  @ViewChild('messageList') private messageList?: ElementRef<HTMLElement>;

  protected readonly minimized = signal(false);
  protected readonly expanded = signal(false);
  protected readonly chatOpen = signal(false);
  protected message = '';

  constructor() {
    effect(() => {
      this.call.messages();
      if (typeof window !== 'undefined') window.setTimeout(() => this.scrollMessages(), 30);
    });
  }

  protected participantName(): string {
    return String(this.call.participant()?.['displayName'] || this.call.participant()?.['userName'] || 'Kullanıcı');
  }

  protected alertName(alert: { participant?: Record<string, unknown> }): string {
    return String(alert.participant?.['displayName'] || alert.participant?.['userName'] || 'Kullanıcı');
  }

  protected restorePanel(): void {
    this.minimized.set(false);
    this.call.reattachVideo();
  }

  protected minimize(): void {
    this.minimized.set(true);
  }

  protected toggleExpanded(): void {
    this.expanded.update((value) => !value);
    this.call.reattachVideo();
  }

  protected toggleChat(): void {
    this.chatOpen.update((value) => !value);
    this.call.reattachVideo();
  }

  protected participantImage(): string {
    const value = String(this.call.participant()?.['profileImage'] || '').trim();
    if (!value) return '';
    const prepared = value.replace('[size]', 's');
    if (/^(https?:|data:|blob:)/.test(prepared)) return prepared;
    return `${this.site.config().apiBaseUrl.replace(/\/+$/, '')}/${prepared.replace(/^\/+/, '')}`;
  }

  protected initials(): string {
    return this.participantName().split(/\s+/).slice(0, 2).map((item) => item.charAt(0)).join('').toLocaleUpperCase('tr-TR') || '?';
  }

  protected statusLabel(): string {
    switch (this.call.mode()) {
      case 'permission': return 'Cihazlar hazırlanıyor';
      case 'incoming': return 'Gelen arama';
      case 'ringing': return 'Aranıyor';
      case 'active': return this.call.call()?.['callType'] === 'video' ? 'Görüntülü görüşme' : 'Sesli görüşme';
      default: return 'Görüşme sona erdi';
    }
  }

  protected async selectImages(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    await this.call.uploadImages(Array.from(input.files || []));
    input.value = '';
  }

  protected async sendMessage(event?: Event): Promise<void> {
    event?.preventDefault();
    if (await this.call.sendMessage(this.message)) {
      this.message = '';
      this.scrollMessages();
    }
  }

  protected sendOnEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey || keyboardEvent.isComposing) return;
    keyboardEvent.preventDefault();
    void this.sendMessage();
  }

  protected startPreviewDrag(event: PointerEvent): void {
    if (typeof window === 'undefined' || !this.localPreview) return;
    const element = this.localPreview.nativeElement;
    const stage = element.parentElement;
    if (!stage) return;
    event.preventDefault();
    element.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startY = event.clientY;
    const startRect = element.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const move = (moveEvent: PointerEvent) => {
      const left = Math.max(8, Math.min(stageRect.width - startRect.width - 8, startRect.left - stageRect.left + moveEvent.clientX - startX));
      const top = Math.max(8, Math.min(stageRect.height - startRect.height - 82, startRect.top - stageRect.top + moveEvent.clientY - startY));
      element.style.left = `${left}px`;
      element.style.top = `${top}px`;
      element.style.right = 'auto';
    };
    const end = (endEvent: PointerEvent) => {
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerup', end);
      element.removeEventListener('pointercancel', end);
      const rect = element.getBoundingClientRect();
      const latestStage = stage.getBoundingClientRect();
      const left = rect.left - latestStage.left;
      if (left > (latestStage.width - rect.width) / 2) {
        element.style.left = 'auto';
        element.style.right = '12px';
      } else {
        element.style.left = '12px';
        element.style.right = 'auto';
      }
    };
    element.addEventListener('pointermove', move);
    element.addEventListener('pointerup', end);
    element.addEventListener('pointercancel', end);
  }

  private scrollMessages(): void {
    const element = this.messageList?.nativeElement;
    if (element) element.scrollTop = element.scrollHeight;
  }
}
